import { useState, useEffect, useCallback } from 'react';
import { useApi } from './useApi';
import { API_BASE_URL, getTenantId } from '../config/api';

export const usePaiements = () => {
  const { apiCall } = useApi();
  const [paiements, setPaiements] = useState([]);
  const [stats, setStats] = useState({
    revenue: {
      transport: 55,
      livraison: 87,
      location: 94,
      conducteur: 65
    },
    totalCommission: 301,
    refundRate: 12,
    failedPayments: 35
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPaiements = useCallback(async (filters = {}) => {
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger les paiements');
      setPaiements([]);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'Tous' && value !== 'Mois') {
          queryParams.append(key, value);
        }
      });

      const endpoint = `/paiements${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const data = await apiCall(endpoint);
      
      setPaiements(data.paiements || []);
      if (data.stats) {
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des paiements:', error);
      setError(error.message);
      setPaiements([]);
    } finally {
      setLoading(false);
    }
  }, [apiCall]);

  const downloadCSV = useCallback(async (paiementId) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const tenantId = user.tenant_id;
      
      const response = await fetch(`${API_BASE_URL}/paiements/${paiementId}/csv`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-tenant-id': tenantId
        }
      });
      
      if (!response.ok) {
        throw new Error('Erreur lors du téléchargement');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `paiement_${paiementId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement CSV:', error);
      throw error;
    }
  }, []);

  useEffect(() => {
    fetchPaiements();
  }, []);

  return {
    paiements,
    stats,
    loading,
    error,
    refetch: fetchPaiements,
    downloadCSV
  };
};

export const usePaiement = (id) => {
  const { apiCall } = useApi();
  const [paiement, setPaiement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPaiement = useCallback(async () => {
    // Vérifier la disponibilité du token et des données utilisateur
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    
    if (!token || !user.tenant_id) {
      console.log('⚠️ Token ou tenant_id manquant, impossible de charger le paiement');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      const data = await apiCall(`/paiements/${id}`);
      setPaiement(data);
    } catch (error) {
      console.error('Erreur lors du chargement du paiement:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [id, apiCall]);

  const updateNote = useCallback(async (note) => {
    try {
      await apiCall(`/paiements/${id}/note`, {
        method: 'PUT',
        body: JSON.stringify({ note })
      });
      setPaiement(prev => ({ ...prev, note }));
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la note:', error);
      throw error;
    }
  }, [id, apiCall]);

  const refund = useCallback(async () => {
    try {
      await apiCall(`/paiements/${id}/refund`, {
        method: 'POST'
      });
      await fetchPaiement();
    } catch (error) {
      console.error('Erreur lors du remboursement:', error);
      throw error;
    }
  }, [id, apiCall, fetchPaiement]);

  useEffect(() => {
    if (id) {
      fetchPaiement();
    }
  }, [id]);

  return {
    paiement,
    loading,
    error,
    updateNote,
    refund,
    refetch: fetchPaiement
  };
};